import React from 'react';
import styled, { css } from 'styled-components';
import { Size, componentSizes } from '~/styles/constants';
import { isEmpty, isNil } from 'ramda';
import { Label, Props as TypographyProps } from '../Typography';
import TEST_ID from './index.testid';
import type { ThemeColor } from '~/theme/System/tokens/colorPalette';

export type Props = {
  /** Label to be displayed if it is passed */
  label?: string | React.ReactNode;

  /** Error message that replaces the label if it is passed */
  error?: string | null;

  /** Size of the label */
  size?: Size;

  required?: boolean;

  color?: ThemeColor;
  margin?: TypographyProps['margin'];
};

/**
 * Label used in inputs and dropdowns
 */
const InputLabel: React.FCC<Props> = ({
  error,
  label,
  size = 'medium',
  required = false,
  color,
  margin,
  ...rest
}) => {
  const hasError = !isNil(error) && !isEmpty(error);

  const errorComponent = hasError && (
    <StyledLabel
      inline
      color={{ group: 'danger' }}
      $size={size}
      dataTestId={TEST_ID.ERROR_LABEL}
      {...rest}
    >
      ({error})
    </StyledLabel>
  );

  return (
    <>
      {label ? (
        <StyledLabel
          required={required}
          inline
          color={color ?? { group: 'text' }}
          $size={size}
          margin={margin || [null, null, 'xxxs', null]}
          dataTestId={TEST_ID.LABEL}
          {...rest}
        >
          {label}
          {errorComponent ? <>&nbsp;{errorComponent}</> : null}
        </StyledLabel>
      ) : (
        errorComponent
      )}
    </>
  );
};

type LabelProps = TypographyProps & {
  $size: Size;
  $hasError?: boolean;
};

const StyledLabel = styled(Label)<LabelProps>(
  ({ $size = 'medium', theme }) => css`
    font-size: ${theme.fontSize(componentSizes[$size].fontSize)};
    font-weight: ${theme.fw('semiBold')};
  `,
);

export default InputLabel;
